import {
  CeilingSettings,
  FixtureKind,
  ObjectType,
  ProjectState,
  RoomDimensions,
  SurfaceId,
  TileCategory,
} from '../types';

export interface RoomPresetTile {
  tileWidth: number;
  tileHeight: number;
  price?: number;
  name?: string;
  sku?: string;
}

export interface RoomPreset {
  id: string;
  name: string;
  description: string;
  /** Краткая характеристика площади для карточки */
  badge: string;
  room: RoomDimensions;
  templates: Partial<Record<SurfaceId, string>>;
  tiles?: Partial<Record<TileCategory, RoomPresetTile>>;
  fixtures?: Array<{ kind: FixtureKind; x: number; z: number; rotation: 0 | 90 | 180 | 270 }>;
  objects?: Array<{
    type: ObjectType;
    surfaceId: Exclude<SurfaceId, 'floor'>;
    width: number;
    height: number;
    x: number;
    y: number;
    swing?: 'left' | 'right';
  }>;
  grout?: { width: number; color?: string };
  ceiling?: CeilingSettings;
  wastePercent?: number;
  /** Ёлочка, калейдоскоп и прочие сложные раскладки требуют запаса */
  hint?: string;
}

export const ROOM_PRESETS: RoomPreset[] = [
  {
    id: 'bathroom',
    name: 'Ванная комната',
    description: 'Классика: тёмный низ, фриз, светлый верх, пол 600×600',
    badge: '3,6 × 2,4 м',
    room: { width: 3600, length: 2400, height: 2700 },
    templates: {
      front: 'classic-dark-light',
      left: 'wall-straight',
      right: 'classic-dark-light',
      back: 'decor-center',
      floor: 'floor-straight',
    },
    tiles: {
      floor: { tileWidth: 600, tileHeight: 600, price: 2390, name: 'Stone Graphite', sku: 'KL-FLO-01' },
      darkWall: { tileWidth: 300, tileHeight: 600, price: 1890, name: 'Slate Night', sku: 'KL-DAR-01' },
      lightWall: { tileWidth: 300, tileHeight: 600, price: 1690, name: 'Linen Pearl', sku: 'KL-LIG-01' },
      decor: { tileWidth: 300, tileHeight: 600, price: 2590, name: 'Botanic Moss', sku: 'KL-DEC-01' },
      border: { tileWidth: 600, tileHeight: 100, price: 790, name: 'Brass Line', sku: 'KL-BOR-01' },
    },
    fixtures: [
      { kind: 'bath', x: 500, z: 1800, rotation: 0 },
      { kind: 'toilet', x: 3100, z: 1900, rotation: 180 },
      { kind: 'sink', x: 1200, z: 350, rotation: 0 },
    ],
    objects: [
      { type: 'door', surfaceId: 'right', width: 800, height: 2050, x: 180, y: 0, swing: 'right' },
      { type: 'window', surfaceId: 'front', width: 700, height: 600, x: 2250, y: 1250 },
    ],
    grout: { width: 3, color: '#d7d2ca' },
    ceiling: { color: '#f4f2ee', glossy: true, spotlights: 4 },
    wastePercent: 10,
  },
  {
    id: 'sanuzel',
    name: 'Санузел',
    description: 'Компактный санузел: ёлочка на полу 150×300, светлые стены',
    badge: '1,5 × 1,8 м',
    room: { width: 1500, length: 1800, height: 2500 },
    templates: {
      front: 'vertical-accent',
      left: 'wall-straight',
      right: 'wall-straight',
      back: 'double-border',
      floor: 'floor-herringbone',
    },
    tiles: {
      floor: { tileWidth: 150, tileHeight: 300, price: 2190, name: 'Ivory Herringbone', sku: 'KL-FLO-02' },
      darkWall: { tileWidth: 300, tileHeight: 600, price: 1750, name: 'Graphite Wall', sku: 'KL-DAR-02' },
      lightWall: { tileWidth: 300, tileHeight: 600, price: 1590, name: 'Snow Gloss', sku: 'KL-LIG-02' },
      decor: { tileWidth: 300, tileHeight: 600, price: 2450, name: 'Aqua Decor', sku: 'KL-DEC-02' },
      border: { tileWidth: 300, tileHeight: 100, price: 690, name: 'Marble Border', sku: 'KL-BOR-02' },
    },
    fixtures: [
      { kind: 'toilet', x: 1150, z: 1500, rotation: 180 },
      { kind: 'sink', x: 350, z: 400, rotation: 0 },
    ],
    objects: [
      { type: 'door', surfaceId: 'front', width: 700, height: 2050, x: 300, y: 0, swing: 'left' },
    ],
    grout: { width: 2, color: '#e2ded6' },
    ceiling: { color: '#f4f2ee', glossy: true, spotlights: 2 },
    wastePercent: 15,
    hint: 'Ёлочка: запас увеличен до 15% — подрезка идёт под углом 45°.',
  },
  {
    id: 'kitchen',
    name: 'Кухня',
    description: 'Модульная раскладка пола с декором, фартук из декора',
    badge: '3,2 × 2,4 м',
    room: { width: 3200, length: 2400, height: 2700 },
    templates: {
      front: 'decor-center',
      left: 'classic-dark-light',
      right: 'wall-straight',
      back: 'wall-straight',
      floor: 'floor-modular',
    },
    tiles: {
      floor: { tileWidth: 600, tileHeight: 600, price: 2650, name: 'Concrete Grey', sku: 'KL-FLO-03' },
      darkWall: { tileWidth: 300, tileHeight: 600, price: 1790, name: 'Deep Clay', sku: 'KL-DAR-03' },
      lightWall: { tileWidth: 300, tileHeight: 600, price: 1720, name: 'Cotto Light', sku: 'KL-LIG-03' },
      decor: { tileWidth: 300, tileHeight: 300, price: 2890, name: 'Zellige Patch', sku: 'KL-DEC-03' },
      border: { tileWidth: 600, tileHeight: 100, price: 850, name: 'Corten Strip', sku: 'KL-BOR-03' },
    },
    fixtures: [
      { kind: 'sink', x: 500, z: 400, rotation: 0 },
    ],
    objects: [
      { type: 'door', surfaceId: 'left', width: 900, height: 2050, x: 300, y: 0, swing: 'right' },
      { type: 'window', surfaceId: 'front', width: 1400, height: 1400, x: 900, y: 1000 },
    ],
    grout: { width: 2, color: '#cfc9c0' },
    ceiling: { color: '#efe6d8', glossy: true, spotlights: 4 },
    wastePercent: 10,
    hint: 'Модульная раскладка использует материал «Декор» в линиях модуля.',
  },
  {
    id: 'pool',
    name: 'Бассейн',
    description: 'Чаша 8 × 4 м, крупный формат 600×1200 по диагонали',
    badge: '8,0 × 4,0 м',
    room: { width: 8000, length: 4000, height: 2900 },
    templates: {
      front: 'wall-straight',
      left: 'wall-straight',
      right: 'wall-straight',
      back: 'wall-straight',
      floor: 'floor-diagonal',
    },
    tiles: {
      floor: { tileWidth: 600, tileHeight: 1200, price: 3450, name: 'Pool Sandstone', sku: 'KL-FLO-04' },
      darkWall: { tileWidth: 300, tileHeight: 600, price: 1890, name: 'Aqua Deep', sku: 'KL-DAR-04' },
      lightWall: { tileWidth: 300, tileHeight: 600, price: 1790, name: 'Aqua Light', sku: 'KL-LIG-04' },
      decor: { tileWidth: 300, tileHeight: 300, price: 2590, name: 'Mosaic Mix', sku: 'KL-DEC-04' },
      border: { tileWidth: 600, tileHeight: 100, price: 990, name: 'Waterline', sku: 'KL-BOR-04' },
    },
    fixtures: [],
    objects: [
      { type: 'door', surfaceId: 'left', width: 1000, height: 2200, x: 1500, y: 0, swing: 'left' },
      { type: 'window', surfaceId: 'front', width: 3000, height: 1200, x: 2500, y: 1400 },
    ],
    grout: { width: 3, color: '#cfd8dc' },
    ceiling: { color: '#e5dccb', glossy: true, spotlights: 4 },
    wastePercent: 15,
    hint: 'Крупный формат 600×1200: запас 15% и клей для тяжёлой плитки считаются автоматически.',
  },
  {
    id: 'shower',
    name: 'Душевая зона',
    description: 'Мозаика 100×100 и калейдоскоп на полу — витрина декора',
    badge: '1,4 × 1,4 м',
    room: { width: 1400, length: 1400, height: 2400 },
    templates: {
      front: 'wall-kaleidoscope',
      left: 'wall-modular',
      right: 'wall-straight',
      back: 'decor-center',
      floor: 'floor-kaleidoscope',
    },
    tiles: {
      floor: { tileWidth: 200, tileHeight: 200, price: 3190, name: 'Mosaic Pearl', sku: 'KL-FLO-06' },
      darkWall: { tileWidth: 300, tileHeight: 600, price: 1890, name: 'Tropic Deep', sku: 'KL-DAR-06' },
      lightWall: { tileWidth: 300, tileHeight: 600, price: 1790, name: 'Tropic Light', sku: 'KL-LIG-06' },
      decor: { tileWidth: 300, tileHeight: 600, price: 2690, name: 'Reef Decor', sku: 'KL-DEC-06' },
      border: { tileWidth: 300, tileHeight: 100, price: 790, name: 'Reef Border', sku: 'KL-BOR-06' },
    },
    fixtures: [
      { kind: 'sink', x: 1180, z: 300, rotation: 90 },
    ],
    objects: [
      { type: 'niche', surfaceId: 'back', width: 600, height: 400, x: 400, y: 1100 },
    ],
    grout: { width: 2, color: '#e8e4dc' },
    ceiling: { color: '#cdd8e2', glossy: true, spotlights: 2 },
    wastePercent: 15,
    hint: 'Калейдоскоп и модульная раскладка используют материал «Декор».',
  },
];

export const getRoomPreset = (id: string): RoomPreset | undefined =>
  ROOM_PRESETS.find((preset) => preset.id === id);

/**
 * Применяет помещение-пресет к проекту: габариты, шаблоны раскладок, форматы плитки,
 * сантехника, проёмы, шов, потолок и запас. Плитки без размеров в пресете сохраняются как есть.
 */
export const applyRoomPreset = (project: ProjectState, preset: RoomPreset): ProjectState => {
  const next: ProjectState = JSON.parse(JSON.stringify(project)) as ProjectState;
  next.name = preset.name;
  next.room = { ...preset.room };
  next.wastePercent = preset.wastePercent ?? project.wastePercent;

  (Object.entries(preset.templates) as Array<[SurfaceId, string]>).forEach(([surfaceId, templateId]) => {
    next.surfaces[surfaceId] = { ...next.surfaces[surfaceId], templateId };
  });

  if (preset.tiles) {
    (Object.entries(preset.tiles) as Array<[TileCategory, RoomPresetTile]>).forEach(([category, tilePreset]) => {
      const existing = next.tiles[category];
      if (!existing) return;
      next.tiles[category] = {
        ...existing,
        tileWidth: tilePreset.tileWidth,
        tileHeight: tilePreset.tileHeight,
        price: tilePreset.price ?? existing.price,
        name: tilePreset.name ?? existing.name,
        sku: tilePreset.sku ?? existing.sku,
        rotation: 0,
      };
    });
  }

  next.fixtures = (preset.fixtures ?? []).map((fixture) => ({
    id: crypto.randomUUID(),
    ...fixture,
  }));

  next.objects = (preset.objects ?? []).map((object) => ({
    id: crypto.randomUUID(),
    ...object,
  }));

  if (preset.grout) {
    next.grout = {
      ...next.grout,
      width: preset.grout.width,
      color: preset.grout.color ?? next.grout.color,
    };
  }

  if (preset.ceiling) next.ceiling = { ...preset.ceiling };
  next.selectedSurface = 'front';
  next.presetId = preset.id;
  next.updatedAt = new Date().toISOString();

  return next;
};
